import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { EntityList, type EntityListColumn } from '@/components/crm/entity-list'
import { KanbanBoard, type KanbanColumn } from '@/components/crm/kanban-board'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/crm/leads/')({
  component: LeadsIndexPage,
})

type LeadStatus = 'new' | 'working' | 'qualified' | 'unqualified' | 'converted'

type LeadRow = {
  id: string
  firstName?: string | null
  lastName?: string | null
  companyName?: string | null
  email?: string | null
  status?: LeadStatus | null
  ownerUserId?: string | null
  createdAt?: string | Date | null
}

const STATUSES: LeadStatus[] = ['new', 'working', 'qualified', 'unqualified', 'converted']

function formatDate(v: string | Date | null | undefined): string {
  if (!v) return '-'
  try {
    return new Date(v).toLocaleDateString()
  } catch {
    return '-'
  }
}

function fullName(row: LeadRow): string {
  return `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || '(no name)'
}

function LeadsIndexPage() {
  const t = useTranslations()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [view, setView] = useState<'list' | 'kanban'>('list')
  const [reassignTarget, setReassignTarget] = useState<string>('')

  const statusLabel = (s: LeadStatus): string => {
    switch (s) {
      case 'new':
        return t('crm.lead.statusNew')
      case 'working':
        return t('crm.lead.statusWorking')
      case 'qualified':
        return t('crm.lead.statusQualified')
      case 'unqualified':
        return t('crm.lead.statusUnqualified')
      case 'converted':
        return t('crm.lead.statusConverted')
    }
  }

  const { data, isLoading } = useQuery(
    orpc.crm.leads.list.queryOptions({ input: { limit: 500, offset: 0 } }),
  )

  const rows: LeadRow[] = (data as LeadRow[] | undefined) ?? []

  const bulkReassign = useMutation(
    orpc.crm.leads.bulkReassign.mutationOptions({
      onSuccess: (result) => {
        toast.success(`Reassigned ${result.reassigned} lead(s)`)
        queryClient.invalidateQueries({ queryKey: orpc.crm.leads.list.key() })
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const columns: EntityListColumn<LeadRow>[] = [
    {
      key: 'name',
      header: t('crm.account.name'),
      render: (r) => <span className="font-medium">{fullName(r)}</span>,
    },
    {
      key: 'companyName',
      header: t('crm.lead.company'),
      render: (r) => r.companyName ?? '-',
    },
    { key: 'email', header: t('crm.lead.email'), render: (r) => r.email ?? '-' },
    {
      key: 'status',
      header: t('crm.common.status'),
      render: (r) => <Badge variant="outline">{statusLabel(r.status ?? 'new')}</Badge>,
    },
    {
      key: 'ownerUserId',
      header: t('crm.common.ownedBy'),
      render: (r) => r.ownerUserId ?? '-',
    },
    {
      key: 'createdAt',
      header: t('crm.common.createdAt'),
      render: (r) => formatDate(r.createdAt ?? null),
    },
  ]

  const kanbanColumns: KanbanColumn<LeadRow>[] = useMemo(
    () =>
      STATUSES.map((status) => {
        const colRows = rows.filter((r) => (r.status ?? 'new') === status)
        return {
          key: status,
          title: <span>{statusLabel(status)}</span>,
          subtitle: `${colRows.length} ${t('crm.lead.entityPlural')}`,
          rows: colRows,
        }
      }),
    [rows],
  )

  const updateMutation = useMutation(
    orpc.crm.leads.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.crm.leads.list.key() })
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const handleMoveCard = (recordId: string, _from: string, to: string) => {
    updateMutation.mutate({ id: recordId, status: to as LeadStatus })
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('crm.lead.entityPlural')}</h1>
        <div className="flex items-center gap-2">
          <Button
            variant={view === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('list')}
          >
            {t('crm.opportunity.viewList')}
          </Button>
          <Button
            variant={view === 'kanban' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('kanban')}
          >
            {t('crm.opportunity.viewKanban')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate({ to: '/crm/leads/import' })}>
            {t('crm.lead.importCsv')}
          </Button>
        </div>
      </div>

      {view === 'list' ? (
        <EntityList
          rows={rows}
          columns={columns}
          searchAccessor={(r) => `${fullName(r)} ${r.companyName ?? ''} ${r.email ?? ''}`}
          searchPlaceholder={`${t('crm.common.search')}…`}
          isLoading={isLoading}
          newButton={{
            label: t('crm.lead.newLead'),
            onClick: () => navigate({ to: '/crm/leads/new' }),
          }}
          onRowClick={(r) => navigate({ to: '/crm/leads/$leadId', params: { leadId: r.id } })}
          bulkActions={[
            {
              key: 'reassign',
              label: t('crm.common.reassign'),
              onClick: (selected) => {
                const target = window.prompt('Reassign to user id:', reassignTarget)
                if (!target) return
                setReassignTarget(target)
                bulkReassign.mutate({
                  ids: selected.map((s) => s.id),
                  ownerUserId: target,
                })
              },
            },
          ]}
        />
      ) : (
        <KanbanBoard
          columns={kanbanColumns}
          renderCard={(r) => (
            <div>
              <div className="font-medium truncate">{fullName(r)}</div>
              {r.companyName && (
                <div className="text-xs text-muted-foreground truncate">{r.companyName}</div>
              )}
            </div>
          )}
          onMoveCard={handleMoveCard}
          onCardClick={(r) => navigate({ to: '/crm/leads/$leadId', params: { leadId: r.id } })}
        />
      )}

      {/*
        Inline helper: the `reassignTarget` prompt-based UX is a
        placeholder until a user picker lands (Phase 6 follow-up). The
        hidden input below keeps React happy with the state hook.
      */}
      <Input type="hidden" value={reassignTarget} readOnly />
    </div>
  )
}
